import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalCommentsService } from "services/ClientPortalCommentsService";
import { CommentModel, CommentThreadRecipientModel } from "services/api/cplapi/CPLAPI";
import { ClientPortalCommentUpdateModel } from "services/classes/ClientPortalCommentUpdateModel";
import { ClientPortalNewCommentModel } from "services/classes/ClientPortalNewCommentModel";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

const initialState = {
  Comments: new Array<CommentModel>(),
  LoadingComments: false,
  EditingComments: false,
  EditingRecipients: false,
  Recipients: new Array<CommentThreadRecipientModel>(),
  LoadingRecipients: false,
};

export const commentThreadSlice = createSlice({
  name: "commentThread",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getComments.pending, (state) => {
        state.LoadingComments = true;
      })
      .addCase(getComments.rejected, (state) => {
        state.LoadingComments = false;
        state.Comments = new Array<CommentModel>();
      })
      .addCase(getComments.fulfilled, (state, action) => {
        state.LoadingComments = false;
        state.Comments = action.payload;
      })
      .addCase(getRecipients.pending, (state) => {
        state.LoadingRecipients = true;
      })
      .addCase(getRecipients.rejected, (state) => {
        state.LoadingRecipients = false;
        state.Recipients = new Array<CommentThreadRecipientModel>();
      })
      .addCase(getRecipients.fulfilled, (state, action) => {
        state.LoadingRecipients = false;
        state.Recipients = action.payload;
      })
      .addCase(createComment.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(createComment.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(createComment.fulfilled, (state) => {
        state.EditingComments = false;
      })
      .addCase(updateComment.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(updateComment.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(updateComment.fulfilled, (state) => {
        state.EditingComments = false;
      })
      .addCase(deleteComment.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(deleteComment.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(deleteComment.fulfilled, (state) => {
        state.EditingComments = false;
      });
  },
});

export const getComments = createAsyncThunk("CommentThread/GetComments", async (commentThreadID: string) => {
  const commentsService = container.get<IClientPortalCommentsService>(SERVICE_TYPES.ClientPortalCommentsService);
  return await commentsService.getComments(commentThreadID);
});

export const getRecipients = createAsyncThunk(
  "CommentThread/GetCommentThreadRecipients",
  async (commentThreadID: string) => {
    const commentsService = container.get<IClientPortalCommentsService>(SERVICE_TYPES.ClientPortalCommentsService);
    return await commentsService.getCommentThreadRecipients(commentThreadID);
  }
);

export const createComment = createAsyncThunk<
  void,
  { newCommentModel: ClientPortalNewCommentModel; commentThreadID: string },
  { state: RootState }
>("CommentThread/CreateComment", async ({ newCommentModel, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<IClientPortalCommentsService>(SERVICE_TYPES.ClientPortalCommentsService);
  await commentsService.createComment(newCommentModel);
  thunkAPI.dispatch(getComments(commentThreadID));
  thunkAPI.dispatch(getRecipients(commentThreadID));
});

export const updateComment = createAsyncThunk<
  void,
  { commentUpdateModel: ClientPortalCommentUpdateModel; commentThreadID: string },
  { state: RootState }
>("CommentThread/UpdateComment", async ({ commentUpdateModel, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<IClientPortalCommentsService>(SERVICE_TYPES.ClientPortalCommentsService);
  await commentsService.updateComment(commentUpdateModel);
  thunkAPI.dispatch(getComments(commentThreadID));
});

export const deleteComment = createAsyncThunk<
  void,
  { commentID: number; commentThreadID: string },
  { state: RootState }
>("CommentThread/DeleteComment", async ({ commentID, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<IClientPortalCommentsService>(SERVICE_TYPES.ClientPortalCommentsService);
  await commentsService.deleteComment(commentID);
  thunkAPI.dispatch(getComments(commentThreadID));
});

export const downloadCommentAttachment = createAsyncThunk(
  "CommentThread/DownloadCommentAttachment",
  async (commentAttachmentID: number) => {
    const commentsService = container.get<IClientPortalCommentsService>(SERVICE_TYPES.ClientPortalCommentsService);
    return await commentsService.downloadCommentAttachment(commentAttachmentID);
  }
);

export const selectComments = (state: RootState) => state.commentThread.Comments;
export const selectLoadingComments = (state: RootState) => state.commentThread.LoadingComments;
export const selectEditingComments = (state: RootState) => state.commentThread.EditingComments;

export const selectRecipients = (state: RootState) => state.commentThread.Recipients;
export const selectLoadingRecipients = (state: RootState) => state.commentThread.LoadingRecipients;
export const selectEditingRecipients = (state: RootState) => state.commentThread.EditingRecipients;

export default commentThreadSlice.reducer;
